import { composeExpertName } from '@/lib/composeExpertName';
import React, { memo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { VerifiedBadge } from '@/components/shared/VerifiedBadge';
import { Card, CardContent } from '@/components/ui/card';

import { MessageButton } from '@/components/MessageButton';
import { countries } from '@/data/countries';
import { capitalizeWords, decodeHtmlEntities, sanitizeBio, cn } from '@/lib/utils';
import { truncateByGraphemes } from '@/utils/languageUtils';
import { useAuth } from '@/contexts/AuthContext';
import { LComponents } from '@/components/i18n';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { useLocalizations } from '@/hooks/useLocalizations';
import { RESPONSIVE, TYPOGRAPHY } from '@/design-system';
import { ORPHAN_SENTINEL_UUID } from '@/constants/config';
import { useLocationById } from '@/hooks/useLocationById';
import { useCountryName } from '@/hooks/useCountryName';

interface UserCardProps {
  expert: {
    id: string;
    display_name?: string | null;
    name?: string | null;
    lastname?: string | null;
    business?: string | null;
    image?: string | null;
    avatar_url?: string | null;
    about?: string | null;
    city?: string | null;
    country?: string | null;
    location_id?: number | null;
    vanity_url?: string | null;
    membership_tier?: string | null;
  };
  showActions?: boolean;
  hideLocation?: boolean;
  maxBioLength?: number;
  /**
   * Whether the user's profile is publicly listed.
   * When false, card is displayed but not clickable to profile.
   */
  profileListed?: boolean;
  /**
   * Pre-translated bio from parent page (metadata-first pattern).
   * When provided, skips runtime translation entirely.
   * Parent pages should batch-fetch from localization_metadata table.
   */
  localizedBio?: string;
  /** When true, the card link opens in a new browser tab */
  openInNewTab?: boolean;
}

/**
 * UserCard Component - Metadata-First Pattern
 * 
 * This component uses pre-translated content passed from parent pages.
 * Runtime translation is FORBIDDEN in card components per localization architecture.
 * 
 * Parent pages must batch-fetch localized bios from localization_metadata
 * and pass them via the localizedBio prop.
 */
export const UserCard = memo(function UserCard({ 
  expert, 
  showActions = true, 
  hideLocation = false, 
  maxBioLength = 400,
  profileListed = true,
  localizedBio,
  openInNewTab,
}: UserCardProps) {
  const { user } = useAuth();
  const { lCommon } = useLocalizations();
  const { trackProfileImpression, trackProfileClick } = useAnalyticsTracking();
  const hasTrackedImpression = useRef(false);

  const cardRef = useRef<HTMLDivElement>(null);

  // Track impression only when card is ≥50% visible (IntersectionObserver)
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !expert.id || expert.id === ORPHAN_SENTINEL_UUID) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedImpression.current) {
          hasTrackedImpression.current = true;
          trackProfileImpression(expert.id);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [expert.id, trackProfileImpression]);

  // Hide sentinel user from card listings (after all hooks)
  if (expert.id === ORPHAN_SENTINEL_UUID) return null;
  
  // STRICT LOCALE MATCHING: Use pre-translated bio only when provided.
  // No fallback to original - if bio localization is expected but missing, bio section is hidden.
  // For English source content (when localization not needed), expert.about is used.
  const displayBioRaw = localizedBio ?? expert.about;
  // location_id is the SOURCE OF TRUTH (per project memory). Legacy `country` is fallback.
  const { isoAlpha2: resolvedIso, name: resolvedName } = useLocationById(expert.location_id);
  const effectiveCountryCode = resolvedIso || expert.country || null;
  const countryInfo = countries.find(c => c.code === effectiveCountryCode);
  // Localized country name in the visitor's language (e.g. "Brasil" on pt pages)
  const localizedCountryName = useCountryName(effectiveCountryCode);
  const countryDisplayName = localizedCountryName || resolvedName || expert.country || null;
  const userImage = expert.image || expert.avatar_url;
  const rawUserName = composeExpertName(expert.display_name, expert.lastname, expert.name) || 
    expert.business || 
    lCommon('labels.expert', { defaultValue: 'Expert' });
  const userName = capitalizeWords(decodeHtmlEntities(rawUserName));
  const profileUrl = expert.vanity_url ? `/${expert.vanity_url}` : `/profile/${expert.id}`;
  const displayBio = displayBioRaw && maxBioLength 
    ? truncateByGraphemes(displayBioRaw, maxBioLength, '…') 
    : displayBioRaw;

  // Build location text
  const locationText = expert.city && countryDisplayName 
    ? `${expert.city}, ${countryDisplayName}`
    : countryDisplayName || null;

  const handleClick = () => {
    if (profileListed) {
      trackProfileClick(expert.id);
    }
  };

  // Render card content
  const cardContent = (
    <Card ref={cardRef} className={cn("overflow-hidden transition-all duration-300 ease-out relative min-h-[150px] sm:min-h-[180px] h-full group", profileListed && "hover:shadow-lg hover:scale-[1.02]")}>
      <CardContent className={`${RESPONSIVE.paddingCardContentWide} h-full flex flex-col space-y-3`}>
          {/* Avatar | Name | Action Icons */}
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex-shrink-0">
              {userImage ? (
                <img 
                  src={userImage} 
                  alt={userName} 
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  onError={(e) => {
                    // Hide broken image and show fallback
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className={cn(
                  TYPOGRAPHY.metricValue,
                  'w-full h-full items-center justify-center text-muted-foreground',
                  userImage ? 'hidden' : 'flex'
                )}
              >
                {userName[0]?.toUpperCase() || '?'}
              </div>
            </div>
            <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className={cn("font-bold text-lg leading-tight truncate transition-colors duration-200 flex items-center gap-1", profileListed && "group-hover:text-primary")}>
                  <span className="truncate">{userName}</span>
                  {expert.membership_tier != null && (
                    <VerifiedBadge size={16} displayName={userName} membershipTier={expert.membership_tier} />
                  )}
                </h3>
                {!hideLocation && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-base">{countryInfo?.flag || '🌍'}</span>
                    <span className={cn(TYPOGRAPHY.cardDescription, 'truncate')}>
                      {locationText || <LComponents id="expert_card.location_not_specified" defaultValue='Location Not Specified' />}
                    </span>
                  </div>
                )}
              </div>
              {/* MessageButton hidden for unlisted profiles */}
              {showActions && profileListed && (
                <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                  <MessageButton 
                    userId={expert.id} 
                    userName={userName}
                    userAvatar={userImage || undefined}
                    userLocationId={expert.location_id || undefined}
                    size="sm"
                    className="h-6 w-6"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Short about - No loading skeleton needed (metadata-first) */}
          <div className="flex-1 min-h-[48px] sm:min-h-[60px]">
            {displayBio ? (
              <p className={cn(TYPOGRAPHY.cardDescription, 'line-clamp-3')}>
                {sanitizeBio(displayBio)}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    );

  // Return with Link wrapper if profile is listed, otherwise just the card
  if (profileListed) {
    return (
      <Link to={profileUrl} onClick={handleClick} className="block" target={openInNewTab ? "_blank" : undefined} rel={openInNewTab ? "noopener noreferrer" : undefined}>
        {cardContent}
      </Link>
    );
  }

  return <div className="block cursor-default">{cardContent}</div>;
});
